// Configuration hub — the shortcut catalog and the pure display logic.
//
// Every setting stays on its own page; this module only says where each
// one lives, who can use it, and how search finds it. No React and no
// network here, so it's unit-tested (catalog.test.ts).
//
// Rules for a card:
//   - `href` is a real page or section. The test reads App.tsx and fails
//     on a dead link.
//   - The gates are the ones the sidebar and the route use: the app's
//     entitlement and app switch (`app`), a plan feature (`entitlement`),
//     the route's or menu row's flag (`featureFlag`), and `adminOnly`.
//     A flag missing from the resolved set passes, as in the sidebar.
//   - `adminOnly` means only owners and admins can change anything there.
//     Organization settings and your own account stay open to everyone.
//   - Superadmin pages live in the platform console and never appear here.

import { getApp, type AppKey } from '../../lib/apps';

export type ConfigCategory =
  | 'organization'
  | 'identity'
  | 'security-apps'
  | 'ai'
  | 'integrations'
  | 'notifications'
  | 'data';

export const CATEGORY_ORDER: ConfigCategory[] = [
  'organization',
  'identity',
  'security-apps',
  'ai',
  'integrations',
  'notifications',
  'data',
];

export const CATEGORY_COPY: Record<ConfigCategory, { title: string; description: string }> = {
  organization: { title: 'Organization', description: 'Your organization’s details, look and plan.' },
  identity: { title: 'Identity and access', description: 'Who can sign in, how they do it and what they can do.' },
  'security-apps': { title: 'Security apps', description: 'How each app watches and protects your organization.' },
  ai: { title: 'AI', description: 'Which AI your organization uses, and the limits around it and your agents.' },
  integrations: { title: 'Integrations', description: 'The tools connected to X-One, in both directions.' },
  notifications: { title: 'Notifications and reporting', description: 'Who hears about what, and what lands in their inbox.' },
  data: { title: 'Data and compliance', description: 'How long data is kept, and what counts toward each standard.' },
};

export interface ConfigCard {
  id: string;
  category: ConfigCategory;
  title: string;
  description: string;
  href: string;
  /** Other words people search for; never shown. */
  keywords?: string[];
  /** Suite app the card belongs to: hidden unless the org has the app. */
  app?: AppKey;
  /** Plan feature the card needs, beyond any app. */
  entitlement?: string;
  /** Hidden when this flag is explicitly off for the org. */
  featureFlag?: string;
  adminOnly?: boolean;
}

const RESILIENCE_FLAG = 'resilience.tenantSurface';
const GATEWAY_FLAG = 'aiGateway.keys';
const INVENTORY_FLAG = 'surface.uamReads';

export const CONFIG_CARDS: ConfigCard[] = [
  // ---------- Organization ----------
  {
    id: 'org-settings',
    category: 'organization',
    title: 'Organization settings',
    description: 'Everything about your organization in one place: details, branding, plan and usage.',
    href: '/settings',
    keywords: ['profile', 'company'],
  },
  {
    id: 'org-branding',
    category: 'organization',
    title: 'Organization & branding',
    description: 'Your organization’s basic details, and how its name, logo and color appear.',
    href: '/settings/general',
    keywords: ['profile', 'logo', 'colour', 'theme', 'white label'],
  },
  {
    id: 'org-plan',
    category: 'organization',
    title: 'Plan & usage',
    description: 'What your plan includes, and how much of this cycle’s AI allowance is used.',
    href: '/settings/plan',
    keywords: ['billing', 'subscription', 'limits', 'seats'],
  },
  {
    id: 'org-beta',
    category: 'organization',
    title: 'Beta features',
    description: 'Try features opened for early access, and leave a beta at any time.',
    href: '/settings/beta',
    keywords: ['preview', 'early access'],
  },
  {
    id: 'org-guided-setup',
    category: 'organization',
    title: 'Guided setup',
    description: 'Step through the basics: invite a teammate, connect one app, see your first signal.',
    href: '/onboarding',
    keywords: ['getting started', 'onboarding'],
  },

  // ---------- Identity and access ----------
  {
    id: 'identity-members',
    category: 'identity',
    title: 'Members & roles',
    description: 'Invite people, change their roles and suspend or restore access.',
    href: '/identity/members',
    keywords: ['users', 'invitations', 'admins', 'permissions'],
    adminOnly: true,
  },
  {
    id: 'identity-sso',
    category: 'identity',
    title: 'Single sign-on',
    description: 'Let people sign in through your identity provider, and optionally require it.',
    href: '/identity/sso',
    keywords: ['sso', 'saml', 'oidc', 'okta', 'entra'],
    adminOnly: true,
  },
  {
    id: 'identity-scim',
    category: 'identity',
    title: 'SCIM provisioning',
    description: 'Let your identity provider add and remove people automatically.',
    href: '/identity/scim',
    keywords: ['user provisioning', 'deprovisioning', 'okta', 'entra'],
    adminOnly: true,
  },
  {
    id: 'identity-mfa',
    category: 'identity',
    title: 'MFA policy',
    description: 'Decide whether members can add a second sign-in step with an authenticator app.',
    href: '/identity/mfa',
    keywords: ['two-factor', '2fa', 'multi-factor', 'authenticator'],
    adminOnly: true,
  },
  {
    id: 'identity-email-domains',
    category: 'identity',
    title: 'Email domains',
    description: 'The domains your organization owns, so its people can join and sign in.',
    href: '/identity/email-domains',
    keywords: ['verify domain', 'dns'],
    adminOnly: true,
  },
  {
    // First of the four cards on /profile: the command palette keeps one
    // entry per page, and this is the name that page should have there.
    id: 'identity-account',
    category: 'identity',
    title: 'Your account',
    description: 'Your personal details, two-step sign-in and the organizations you belong to.',
    href: '/profile',
    keywords: ['profile', 'password', 'mfa'],
  },
  {
    id: 'identity-api-tokens',
    category: 'identity',
    title: 'API tokens',
    description: 'Your personal tokens for the command line and automation, on your account page.',
    href: '/profile',
    keywords: ['personal access token', 'cli', 'pat'],
  },

  // ---------- Security apps ----------
  {
    id: 'xes-settings',
    category: 'security-apps',
    title: 'Email Security settings',
    description: 'Every Email Security setting, with the current state of each.',
    href: '/xes/settings',
    keywords: ['xes'],
    app: 'xes',
    adminOnly: true,
  },
  {
    id: 'xes-mailboxes',
    category: 'security-apps',
    title: 'Mailboxes',
    description: 'The Microsoft 365 and Gmail mailboxes we scan, and whether each one is working.',
    href: '/xes/settings/mailboxes',
    keywords: ['xes', 'outlook', 'google'],
    app: 'xes',
    adminOnly: true,
  },
  {
    id: 'xes-verdicts',
    category: 'security-apps',
    title: 'Verdict guidance',
    description: 'Extra guidance, in your own words, for how messages are judged.',
    href: '/xes/settings/verdicts',
    keywords: ['xes', 'ai verdict', 'prompt'],
    app: 'xes',
    adminOnly: true,
  },
  {
    id: 'xes-attachments',
    category: 'security-apps',
    title: 'Attachment analysis',
    description: 'Look up attachments against a malware database, and open suspicious ones in a sandbox.',
    href: '/xes/settings/attachments',
    keywords: ['xes', 'virustotal', 'detonation'],
    app: 'xes',
    adminOnly: true,
  },
  {
    id: 'xes-rules',
    category: 'security-apps',
    title: 'Sender & imported rules',
    description: 'Senders always let through or quarantined, and detection rules you bring in.',
    href: '/xes/settings/rules',
    keywords: ['xes', 'allow list', 'block list'],
    app: 'xes',
    adminOnly: true,
  },
  {
    id: 'xes-training',
    category: 'security-apps',
    title: 'Training after a blocked email',
    description: 'The training assigned automatically to the person a quarantined message was sent to.',
    href: '/xes/settings/training',
    keywords: ['xes', 'auto assign'],
    app: 'xes',
    adminOnly: true,
  },
  {
    id: 'posture-settings',
    category: 'security-apps',
    title: 'Posture settings',
    description: 'Settings for Posture, including the safety check before any one-click fix.',
    href: '/posture/settings',
    keywords: ['regression gate', 'remediation'],
    app: 'posture',
    adminOnly: true,
  },
  {
    id: 'posture-accounts',
    category: 'security-apps',
    title: 'Connected accounts',
    description: 'Every cloud, SaaS, virtualization, device and email service you’ve connected, with its status.',
    href: '/posture/cloud-accounts',
    keywords: ['aws', 'azure', 'gcp', 'cloud accounts'],
    app: 'posture',
    adminOnly: true,
  },
  {
    id: 'posture-agents',
    category: 'security-apps',
    title: 'Posture agents',
    description: 'The agents that work on your Posture results, such as vulnerability scanners and fixes.',
    href: '/posture/agents',
    keywords: ['remediation', 'scanners'],
    app: 'posture',
    adminOnly: true,
  },
  {
    id: 'surface-settings',
    category: 'security-apps',
    title: 'Surface settings',
    description: 'Every Surface setting, with the current state of each.',
    href: '/surface/settings',
    keywords: ['attack surface'],
    app: 'surface',
    adminOnly: true,
  },
  {
    id: 'surface-external-scanning',
    category: 'security-apps',
    title: 'External scanning',
    description: 'The domains we watch from the outside, and how often we scan them in depth.',
    href: '/surface/settings/external-scanning',
    keywords: ['domains', 'breached accounts', 'lookalike'],
    app: 'surface',
    adminOnly: true,
  },
  {
    id: 'surface-entra-id',
    category: 'security-apps',
    title: 'Entra ID scanning',
    description: 'The Microsoft Entra ID connection we use to find risky accounts, and how often we scan it.',
    href: '/surface/settings/entra-id',
    keywords: ['azure ad', 'internal risk'],
    app: 'surface',
    adminOnly: true,
  },
  {
    id: 'surface-source-schedules',
    category: 'security-apps',
    title: 'Source sync schedules',
    description: 'How often each connected source syncs into your inventory.',
    href: '/surface/settings/sources',
    keywords: ['inventory'],
    app: 'surface',
    featureFlag: INVENTORY_FLAG,
    adminOnly: true,
  },
  {
    id: 'surface-repositories-settings',
    category: 'security-apps',
    title: 'Repository settings',
    description: 'Whether each code host is healthy, how often it syncs, and write access for fix pull requests.',
    href: '/surface/settings/repositories',
    keywords: ['github', 'gitlab', 'bitbucket', 'azure devops'],
    app: 'surface',
    featureFlag: INVENTORY_FLAG,
    adminOnly: true,
  },
  {
    id: 'surface-code-scanning',
    category: 'security-apps',
    title: 'Code scanning',
    description: 'Whether we scan your repositories for weaknesses and committed secrets, and how often.',
    href: '/surface/settings/code-scanning',
    keywords: ['sast', 'secrets', 'allowance'],
    app: 'surface',
    featureFlag: INVENTORY_FLAG,
    adminOnly: true,
  },
  {
    id: 'surface-sboms',
    category: 'security-apps',
    title: 'SBOM uploads',
    description: 'Tokens your build pipeline uses to upload SBOMs, and the steps to paste into it.',
    href: '/surface/settings/sboms',
    keywords: ['software bill of materials', 'ci'],
    app: 'surface',
    featureFlag: 'surface.sbom',
    adminOnly: true,
  },
  {
    id: 'exposure-settings',
    category: 'security-apps',
    title: 'Exposure settings',
    description: 'Your SLA targets, who approves exceptions, and how exposures are scored.',
    href: '/exposure/settings',
    keywords: ['vulnerabilities', 'sla', 'scoring weights'],
    app: 'exposure',
    adminOnly: true,
  },
  {
    id: 'exposure-baselines',
    category: 'security-apps',
    title: 'Baselines',
    description: 'Your own policies and contract requirements, measured against the controls you already track.',
    href: '/exposure/baselines',
    keywords: ['custom compliance', 'requirements', 'evidence', 'new baseline'],
    app: 'exposure',
    adminOnly: true,
  },
  {
    id: 'exposure-queries',
    category: 'security-apps',
    title: 'Tracked queries',
    description: 'The asset questions you track every day, and whether admins hear when a count rises.',
    href: '/exposure/queries',
    keywords: ['saved queries', 'edr coverage', 'alerts', 'dashboard'],
    app: 'exposure',
    adminOnly: true,
  },
  {
    id: 'resilience-settings',
    category: 'security-apps',
    title: 'Resilience settings',
    description: 'Every Resilience setting, with the current state of each.',
    href: '/resilience/settings',
    keywords: ['backup', 'connect saas app', 'connect cloud account'],
    app: 'resilience',
    featureFlag: RESILIENCE_FLAG,
    adminOnly: true,
  },
  {
    id: 'resilience-sources',
    category: 'security-apps',
    title: 'Backup sources',
    description: 'Every source you back up, and whether its next backup will run.',
    href: '/resilience/settings/sources',
    keywords: ['credentials', 'vault', 'microsoft 365', 'google workspace'],
    app: 'resilience',
    featureFlag: RESILIENCE_FLAG,
    adminOnly: true,
  },
  {
    id: 'umbra-settings',
    category: 'security-apps',
    title: 'Umbra settings',
    description: 'Who can use Umbra through X-One, and the highest role they can have.',
    href: '/umbra/settings',
    keywords: ['remote access', 'manager access', 'role limit'],
    app: 'umbra',
    adminOnly: true,
  },
  {
    id: 'automated-response-settings',
    category: 'security-apps',
    title: 'Automated response settings',
    description: 'Defaults for rules that act on risky behavior, such as cooldowns and fixes.',
    href: '/policies/automated-responses/settings',
    keywords: ['policy engine', 'remediation', 'cooldown'],
    entitlement: 'policyEngine',
    adminOnly: true,
  },

  // ---------- AI ----------
  {
    id: 'gateway-settings',
    category: 'ai',
    title: 'AI Gateway settings',
    description: 'The projects your API keys belong to, so requests and spend are tracked per project.',
    href: '/gateway/settings',
    keywords: ['projects'],
    app: 'ai-gateway',
    featureFlag: GATEWAY_FLAG,
    adminOnly: true,
  },
  {
    id: 'gateway-providers',
    category: 'ai',
    title: 'AI providers',
    description: 'The AI providers and models your organization can use through the gateway.',
    href: '/gateway/providers',
    keywords: ['gateway', 'models', 'openai', 'anthropic', 'bedrock'],
    app: 'ai-gateway',
    featureFlag: GATEWAY_FLAG,
    adminOnly: true,
  },
  {
    id: 'gateway-routes',
    category: 'ai',
    title: 'AI routes',
    description: 'Which provider answers each model name, which to fall back to, and which policy applies.',
    href: '/gateway/routes',
    keywords: ['gateway', 'fallback', 'routing'],
    app: 'ai-gateway',
    featureFlag: GATEWAY_FLAG,
    adminOnly: true,
  },
  {
    id: 'gateway-guardrails',
    category: 'ai',
    title: 'AI guardrails',
    description: 'Prompt-injection defense, redaction of personal data and secrets, tool approvals and agent budgets.',
    href: '/gateway/policies',
    keywords: ['gateway', 'policies', 'data loss'],
    app: 'ai-gateway',
    featureFlag: GATEWAY_FLAG,
    adminOnly: true,
  },
  {
    id: 'gateway-keys',
    category: 'ai',
    title: 'AI Gateway API keys',
    description: 'The keys your applications use to call the gateway.',
    href: '/gateway/keys',
    keywords: ['tokens'],
    app: 'ai-gateway',
    featureFlag: GATEWAY_FLAG,
    adminOnly: true,
  },
  {
    id: 'gateway-identities',
    category: 'ai',
    title: 'AI Gateway identities',
    description: 'Let tokens from your own identity issuers call the gateway, mapped to a project and policy.',
    href: '/gateway/identities',
    keywords: ['issuers', 'workload identity'],
    app: 'ai-gateway',
    featureFlag: GATEWAY_FLAG,
    adminOnly: true,
  },
  {
    id: 'gateway-spend',
    category: 'ai',
    title: 'AI spend caps',
    description: 'Your AI Gateway plan, and the caps that keep spending in check.',
    href: '/gateway/billing',
    keywords: ['gateway', 'billing', 'budget'],
    app: 'ai-gateway',
    featureFlag: GATEWAY_FLAG,
    adminOnly: true,
  },
  {
    id: 'agents-installed',
    category: 'ai',
    title: 'Agents',
    description: 'The agents running for your organization, and how each one is set up.',
    href: '/agents/installed',
    keywords: ['autonomous response', 'installed'],
    app: 'agents',
    adminOnly: true,
  },
  {
    id: 'agents-approvals',
    category: 'ai',
    title: 'Agent approvals',
    description: 'Agent and workflow actions that wait for an admin to approve them.',
    href: '/agents/approvals',
    keywords: ['human in the loop'],
    app: 'agents',
    adminOnly: true,
  },
  {
    id: 'orchestrate-workflows',
    category: 'ai',
    title: 'Agent workflows',
    description: 'Combine your installed agents into workflows that respond to events.',
    href: '/orchestrate',
    keywords: ['orchestration'],
    app: 'orchestrate',
    featureFlag: 'app.orchestrate',
    adminOnly: true,
  },
  {
    id: 'insights-instructions',
    category: 'ai',
    title: 'AI Insights instructions',
    description: 'Tell AI Insights what your organization cares about, at the top of its page.',
    href: '/insights',
    keywords: ['digest', 'prompt', 'customization'],
    entitlement: 'aiInsights',
    featureFlag: 'ai.insights',
    adminOnly: true,
  },

  // ---------- Integrations ----------
  {
    id: 'integrations-connectors',
    category: 'integrations',
    title: 'Connectors',
    description: 'Connect your security tools, and see which connections are working.',
    href: '/integrations/connectors',
    keywords: ['catalog', 'vendors', 'tools'],
    adminOnly: true,
  },
  {
    id: 'integrations-webhooks',
    category: 'integrations',
    title: 'Webhooks',
    description: 'Send events to systems you control, and check that they arrive.',
    href: '/integrations/webhooks',
    keywords: ['events', 'https'],
    adminOnly: true,
  },
  {
    id: 'integrations-credentials',
    category: 'integrations',
    title: 'OAuth credentials',
    description: 'Sign-in connections for your integrations. Test or disconnect them here.',
    href: '/integrations/credentials',
    keywords: ['tokens', 'reconnect', 'servicenow'],
    adminOnly: true,
  },
  {
    id: 'integrations-siem',
    category: 'integrations',
    title: 'SIEM',
    description: 'Stream every event to your security monitoring.',
    href: '/integrations/connectors#siem',
    keywords: ['splunk', 'sentinel', 'logs'],
    adminOnly: true,
  },
  {
    id: 'integrations-ticketing',
    category: 'integrations',
    title: 'Ticketing',
    description: 'Open tickets automatically for things that need work.',
    href: '/integrations/connectors#tickets',
    keywords: ['servicenow', 'jira'],
    adminOnly: true,
  },
  {
    id: 'integrations-cmdb',
    category: 'integrations',
    title: 'CMDB',
    description: 'How your inventory is compared with your CMDB, and how differences are handled.',
    href: '/surface/settings/cmdb',
    keywords: ['reconciliation', 'servicenow', 'write back'],
    app: 'surface',
    featureFlag: INVENTORY_FLAG,
    adminOnly: true,
  },
  {
    id: 'integrations-inventory-sources',
    category: 'integrations',
    title: 'Inventory sources',
    description: 'The tools that feed your asset inventory, and adding one from any other tool.',
    href: '/surface/sources',
    keywords: ['surface', 'assets', 'csv'],
    app: 'surface',
    featureFlag: INVENTORY_FLAG,
    adminOnly: true,
  },
  {
    id: 'integrations-repositories',
    category: 'integrations',
    title: 'Code repositories',
    description: 'Connect GitHub, GitLab, Bitbucket or Azure DevOps.',
    href: '/surface/repositories',
    keywords: ['surface', 'source code'],
    app: 'surface',
    featureFlag: INVENTORY_FLAG,
    adminOnly: true,
  },
  {
    id: 'integrations-umbra-connectors',
    category: 'integrations',
    title: 'Umbra connectors',
    description: 'Check that the connectors beside your hidden systems are healthy and reporting in.',
    href: '/umbra/connectors',
    keywords: ['remote access'],
    app: 'umbra',
    adminOnly: true,
  },

  // ---------- Notifications and reporting ----------
  {
    id: 'notifications-posture-routing',
    category: 'notifications',
    title: 'Posture alert routing',
    description: 'Send new alerts, resolutions and expiring exemptions to the tools your team uses.',
    href: '/posture/outbound',
    keywords: ['slack', 'teams', 'servicenow', 'jira', 'splunk', 'outbound'],
    app: 'posture',
    adminOnly: true,
  },
  {
    id: 'notifications-email-security',
    category: 'notifications',
    title: 'Email Security notifications',
    description: 'Where verdicts and quarantines are posted, such as Slack, Teams or a webhook.',
    href: '/xes/settings/integrations',
    keywords: ['xes', 'alerts', 'extensions'],
    app: 'xes',
    adminOnly: true,
  },
  {
    id: 'notifications-chat',
    category: 'notifications',
    title: 'Chat alerts',
    description: 'Post alerts to the channels your team watches.',
    href: '/integrations/connectors#notifications',
    keywords: ['slack', 'teams'],
    adminOnly: true,
  },
  {
    id: 'notifications-insights-digest',
    category: 'notifications',
    title: 'AI Insights weekly email',
    description: 'Choose whether you get the weekly digest, on your account page.',
    href: '/profile',
    keywords: ['digest', 'email preferences'],
    entitlement: 'aiInsights',
  },
  {
    id: 'notifications-posture-digest',
    category: 'notifications',
    title: 'Weekly Posture email',
    description: 'Preview the weekly Posture digest from the Posture overview.',
    href: '/posture/overview',
    keywords: ['digest'],
    app: 'posture',
    adminOnly: true,
  },
  {
    id: 'notifications-scheduled-reports',
    category: 'notifications',
    title: 'Scheduled reports',
    description: 'Email a KPI dashboard on a schedule. Open a dashboard to set up its schedule.',
    href: '/kpi',
    keywords: ['recurring', 'kpi'],
    adminOnly: true,
  },

  // ---------- Data and compliance ----------
  {
    id: 'data-retention-changes',
    category: 'data',
    title: 'Backup retention changes',
    description: 'Requests to shorten how long a backup is kept, waiting for a second person’s approval.',
    href: '/resilience/settings/retention-changes',
    keywords: ['retention'],
    app: 'resilience',
    featureFlag: RESILIENCE_FLAG,
    adminOnly: true,
  },
  {
    id: 'data-retention-ledger',
    category: 'data',
    title: 'Retention ledger',
    description: 'Every retention change, who asked and who approved. Nothing here can be edited.',
    href: '/resilience/settings/retention-ledger',
    keywords: ['audit', 'backup'],
    app: 'resilience',
    featureFlag: RESILIENCE_FLAG,
    adminOnly: true,
  },
  {
    id: 'data-scope-cloud',
    category: 'data',
    title: 'Cloud compliance scope',
    description: 'What each framework applies to across your cloud accounts, what’s excluded and why.',
    href: '/posture/iaas/compliance',
    keywords: ['iso 27001', 'soc 2', 'out of scope'],
    app: 'posture',
    adminOnly: true,
  },
  {
    id: 'data-scope-saas',
    category: 'data',
    title: 'SaaS compliance scope',
    description: 'What each framework applies to across your business apps, what’s excluded and why.',
    href: '/posture/saas/compliance',
    keywords: ['iso 27001', 'soc 2', 'out of scope'],
    app: 'posture',
    adminOnly: true,
  },
  {
    id: 'data-scope-virtualization',
    category: 'data',
    title: 'Virtualization compliance scope',
    description: 'What each framework applies to across your virtualization platforms, what’s excluded and why.',
    href: '/posture/hypervisor/compliance',
    keywords: ['iso 27001', 'soc 2', 'out of scope'],
    app: 'posture',
    adminOnly: true,
  },
  {
    id: 'data-personal-export',
    category: 'data',
    title: 'Download my data',
    description: 'Download every record we hold about you, or close your account, on your account page.',
    href: '/profile',
    keywords: ['gdpr', 'privacy', 'delete account'],
  },
];

// Email Security's short name is its SKU; everywhere else in the UI it's
// spelled out.
export function appLabel(app: AppKey): string {
  return app === 'xes' ? 'Email Security' : getApp(app).shortName;
}

// ---------- visibility ----------

export interface ConfigViewer {
  /** Organization owner or admin. */
  isAdmin: boolean;
  /** The org's entitlements; null when they couldn't be read, which hides
   *  everything that needs one rather than guessing. */
  entitlements: Readonly<Record<string, unknown>> | null;
  /** Flags present in the resolved set and disabled for this org. */
  disabledFlags: ReadonlySet<string>;
}

/** The sidebar's rule: an app needs its entitlement and its app switch
 *  (`app.<key>`, the launcher's rule); a row needs its flag not to be
 *  off and, when marked, an owner or admin. */
export function isCardVisible(card: ConfigCard, viewer: ConfigViewer): boolean {
  if (card.adminOnly && !viewer.isAdmin) return false;
  if (card.featureFlag && viewer.disabledFlags.has(card.featureFlag)) return false;
  if (card.app) {
    const key = getApp(card.app).entitlementKey;
    if (!viewer.entitlements?.[key] || viewer.disabledFlags.has(key)) return false;
  }
  if (card.entitlement && !viewer.entitlements?.[card.entitlement]) return false;
  return true;
}

export function visibleCards(cards: readonly ConfigCard[], viewer: ConfigViewer): ConfigCard[] {
  return cards.filter((c) => isCardVisible(c, viewer));
}

// ---------- search ----------

/** Every word of the query has to appear somewhere on the card, its
 *  search words, its category or its app. */
export function matchesQuery(card: ConfigCard, query: string): boolean {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const hay = [
    card.title,
    card.description,
    ...(card.keywords ?? []),
    CATEGORY_COPY[card.category].title,
    card.app ? appLabel(card.app) : '',
  ]
    .join(' ')
    .toLowerCase();
  return words.every((w) => hay.includes(w));
}

/** Matching cards by category, in category order, empty groups dropped.
 *  `only` narrows to one category. */
export function groupCards(
  cards: readonly ConfigCard[],
  query: string,
  only: ConfigCategory | null = null,
): Array<{ category: ConfigCategory; cards: ConfigCard[] }> {
  return CATEGORY_ORDER.filter((category) => !only || category === only)
    .map((category) => ({
      category,
      cards: cards.filter((c) => c.category === category && matchesQuery(c, query)),
    }))
    .filter((g) => g.cards.length > 0);
}

// ---------- category links ----------

export const CONFIGURATION_PATH = '/configuration';

/** The category a location.hash names (/configuration#ai shows just that
 *  category, so a link can be shared); anything else shows them all. */
export function categoryFromHash(hash: string): ConfigCategory | null {
  const id = hash.replace(/^#/, '');
  return (CATEGORY_ORDER as string[]).includes(id) ? (id as ConfigCategory) : null;
}
